import type { ComponentProps, RefObject } from 'react';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { styled } from '@compiled/react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/button';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditorExpandIcon from '@atlaskit/icon/core/grow-horizontal';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import type { Placement } from '@atlaskit/popper/popper';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Stack from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';

import { UnsplashSearch } from '@atlassian/company-hub-common/media-components/search';
import type { UnsplashResult } from '@atlassian/company-hub-common/media-components/unsplash/types';
import { RovoHeaderImageGenerator } from '@atlassian/ai-image-generation/rovo-header-image-generator';
import type { GenerateImageProps } from '@atlassian/ai-image-generation/types';
import { eventHub } from '@atlassian/editor-plugin-ai/EventHub';

import { COVER_PICTURE_UNSPLASH_UPLOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { usePageState } from '@confluence/page-context';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import type { COVERPICTUREWIDTH } from './contentTopperConstants.ts';
import { FullWidthDropdownMenu } from './FullWidthDropdownMenu.tsx';
import { downloadUnsplashPhoto } from './unsplashAPI.ts';
import {
	buildCoverPromptSuggestionInstruction,
	getTargetEditorIdFromDocument,
	sanitizeSuggestedPrompt,
} from './utils/headerImageAIPromptSuggestionsHelpers.ts';

type UnsplashPopupMenuProps = {
	imageSrc?: string;
	imageAltText?: string;
	initialUnsplashInfoArray: UnsplashResult[] | undefined;
	closePopup: () => void;
	openMediaBrowser: any;
	coverPictureState: any;
	setIsUploadingPicture: (isUploadingPicture: boolean) => void;
	handleUnsplashUpload: (url: string) => void;
	onDelete?: () => void;
	onReposition?: () => void;
	dropdownRef?: RefObject<HTMLElement | null>;
	dropdownPopupComponentRef?: RefObject<HTMLElement | null>;
	placement?: Placement;
	userOffline?: boolean;
	contentId: string;
	isPageTitleComponent?: boolean;
	onImageWidthChange?: (coverPictureWidth: COVERPICTUREWIDTH) => void;
	onCoverImageWidthDropdownOptionSelected?: (isCoverImageWidthChanged: boolean) => void;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<string | undefined>;
};

const i18n = defineMessages({
	popupTitleLabel: {
		id: 'content-topper.page-title.popup.label',
		defaultMessage: 'Header image',
		description:
			'Title of a popup that triggers when a user interacts with the add cover image button in the editor',
	},
	popupTitleLabelSubtext: {
		id: 'content-topper.page-title.popup.label.subtext',
		defaultMessage: 'We recommend images 1100px or wider.',
		description:
			'Helpful hint for a popup that triggers when a user interacts with the add cover image button in the editor',
	},
	repositionButton: {
		id: 'content-topper.page-title.popup.reposition',
		defaultMessage: 'Reposition',
		description: 'Text for resposition button to control header image',
	},
	removeHeaderButton: {
		id: 'content-topper.page-title.popup.remove-image',
		defaultMessage: 'Remove image',
		description: 'Text for removing header image button to delete the picture',
	},
	suggestPromptLabel: {
		id: 'content-topper.page-title.popup.suggest-image-prompt',
		defaultMessage: 'Suggest',
		description: 'Button label to suggest an AI image prompt based on page content',
	},
	suggestPromptTooltip: {
		id: 'content-topper.page-title.popup.suggest-image-prompt.tooltip',
		defaultMessage: 'Suggest from page content',
		description: 'Tooltip for the button that suggests an AI image prompt based on page content',
	},
});

const PopupWrapperStyles = xcss({
	padding: 'space.200',
	width: '280px',
	userSelect: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DeleteButtonWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		paddingLeft: '0px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.text.danger')} !important`,
		marginTop: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.text.danger')} !important`,
	},
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PositionIconWrapper = styled.div({
	transform: 'rotate(-90deg)',
	margin: '2px 0 0 -3px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuOptionsWrapper = styled.div({
	textAlign: 'left',
});

type UnsplashSearchProps = ComponentProps<typeof UnsplashSearch>;

export const UnsplashPopupMenuContent = ({
	imageSrc,
	imageAltText,
	initialUnsplashInfoArray,
	openMediaBrowser,
	coverPictureState,
	closePopup,
	setIsUploadingPicture,
	handleUnsplashUpload,
	onReposition,
	onDelete,
	dropdownRef,
	dropdownPopupComponentRef,
	userOffline,
	contentId,
	isPageTitleComponent,
	onImageWidthChange,
	onCoverImageWidthDropdownOptionSelected,
	generateAIHeaderImage,
}: UnsplashPopupMenuProps): React.JSX.Element => {
	const intl = useIntl();
	const [{ spaceKey }] = usePageState();
	const experienceTracker = useContext(ExperienceTrackerContext) as ExperienceTrackerAPI;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isSuggestingPrompt, setIsSuggestingPrompt] = useState(false);
	const [suggestedPrompt, setSuggestedPrompt] = useState<string>('');
	const mountedRef = useRef(true);

	// Ensure we don't set state after unmount (popup can close quickly).
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const fireSuggestHeaderImageButtonAnalytics = useCallback(
		({
			isButtonDisabled,
			isEditorIdMissing,
		}: {
			isButtonDisabled: boolean;
			isEditorIdMissing: boolean;
		}) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					source: 'unsplashPopupMenu',
					actionSubject: 'button',
					actionSubjectId: 'headerImagePromptSuggestion',
					attributes: {
						isButtonDisabled,
						isEditorIdMissing,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const fireSuggestHeaderImagePromptResultAnalytics = useCallback(
		({
			promptGenerationSuccessful,
			resultType,
			isRegeneratingPrompt,
		}: {
			promptGenerationSuccessful: boolean;
			resultType: string;
			isRegeneratingPrompt: boolean;
		}) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					actionSubject: 'headerImagePromptSuggestionGeneration',
					action: promptGenerationSuccessful && resultType === 'complete' ? 'completed' : 'failed',
					source: 'headerImagePromptSuggestionButton',
					attributes: {
						promptGenerationSuccessful,
						resultType,
						isRegeneratingPrompt,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handleSuggestImagePromptClick = useCallback(() => {
		const isButtonDisabled = userOffline || isSuggestingPrompt;

		const targetEditorId = getTargetEditorIdFromDocument();
		fireSuggestHeaderImageButtonAnalytics({ isButtonDisabled, isEditorIdMissing: !targetEditorId });

		if (isButtonDisabled || !targetEditorId) {
			return;
		}

		const isRegenerating = Boolean(suggestedPrompt?.trim());
		setIsSuggestingPrompt(true);
		setSuggestedPrompt('');

		eventHub.publishToEditor({
			event: 'start prompt',
			data: {
				targetEditorId,
				analyticSourceId: 'confluenceCoverImagePromptSuggestionPopup',
				prompt: 'custom prompt',
				promptText: buildCoverPromptSuggestionInstruction(),
				adjustSelection: 'fullDocument',
				hideModal: true,
				streamingCallback: (data: any) => {
					if (data.type === 'complete') {
						const suggestion = sanitizeSuggestedPrompt(data.result ?? '');
						if (mountedRef.current) {
							setIsSuggestingPrompt(false);
							setSuggestedPrompt(suggestion);
							fireSuggestHeaderImagePromptResultAnalytics({
								promptGenerationSuccessful: true,
								resultType: data.type,
								isRegeneratingPrompt: isRegenerating,
							});
						} else {
							fireSuggestHeaderImagePromptResultAnalytics({
								promptGenerationSuccessful: false,
								resultType: 'not mounted',
								isRegeneratingPrompt: isRegenerating,
							});
						}
					} else if (data.type === 'error' || data.type === 'response too similar') {
						if (mountedRef.current) {
							setIsSuggestingPrompt(false);
						}
						fireSuggestHeaderImagePromptResultAnalytics({
							promptGenerationSuccessful: false,
							resultType: data.type,
							isRegeneratingPrompt: isRegenerating,
						});
					}
				},
			},
		});
	}, [
		userOffline,
		isSuggestingPrompt,
		fireSuggestHeaderImagePromptResultAnalytics,
		fireSuggestHeaderImageButtonAnalytics,
		suggestedPrompt,
	]);

	const onUnsplashImageClick = useCallback(
		async (url: any, downloadLink: any) => {
			experienceTracker.start({
				name: COVER_PICTURE_UNSPLASH_UPLOAD_EXPERIENCE,
			});
			await downloadUnsplashPhoto(downloadLink);
			handleUnsplashUpload(url);
			setIsUploadingPicture(true);
			void coverPictureState.setIsUploadingPicture(true);
			closePopup();
		},
		[experienceTracker, closePopup, coverPictureState, handleUnsplashUpload, setIsUploadingPicture],
	);

	const handleImageSelection: UnsplashSearchProps['handleImageSelection'] = useCallback(
		async ({ fullSizeImageUrl, downloadLink }) => {
			await onUnsplashImageClick(fullSizeImageUrl, downloadLink);
			void coverPictureState.setIsChangingPicture(false);
		},
		[onUnsplashImageClick, coverPictureState],
	);

	return (
		// prevent the unsplash search nested popup from closing
		<Stack
			ref={dropdownRef as RefObject<HTMLDivElement>}
			xcss={PopupWrapperStyles}
			space="space.100"
		>
			<UnsplashSearch
				selectedImageUrl={imageSrc}
				imageAltText={imageAltText}
				initialUnsplashResults={initialUnsplashInfoArray}
				idPrefix="unsplash-popup"
				helperMessage={<FormattedMessage {...i18n.popupTitleLabelSubtext} />}
				handleImageSelection={handleImageSelection}
				openMediaBrowser={openMediaBrowser}
				sectionLabel={<FormattedMessage {...i18n.popupTitleLabel} />}
				dropdownPopupComponentRef={dropdownPopupComponentRef as RefObject<HTMLDivElement>}
				isDisabled={userOffline}
				contentId={contentId}
				isPageTitleComponent={isPageTitleComponent}
				isNoImageAllowed={false}
				analyticsSource="editorPageCoverPicture"
				analyticsActionSubject="headerImageUpload"
				analyticsActionSubjectId="headerImageUploadButtonClicked"
				enableBottomSpacing={false}
			/>
			{!isCompanyHubSpaceKey(spaceKey) && typeof onImageWidthChange === 'function' ? (
				<FullWidthDropdownMenu
					onImageWidthChange={onImageWidthChange}
					userOffline={userOffline}
					selectedWidth={coverPictureState.state.coverPictureWidth}
					onCoverImageWidthDropdownOptionSelected={onCoverImageWidthDropdownOptionSelected}
				/>
			) : null}
			{typeof onReposition === 'function' ? (
				<Button
					iconBefore={
						<PositionIconWrapper>
							<EditorExpandIcon label="" spacing="none" />
						</PositionIconWrapper>
					}
					testId="reposition-image-button"
					isDisabled={userOffline}
					onClick={onReposition}
					shouldFitContainer
				>
					<MenuOptionsWrapper>
						<FormattedMessage {...i18n.repositionButton} />
					</MenuOptionsWrapper>
				</Button>
			) : null}
			{isExperimentEnabled('cc_page_experiences_ai_header_image_generation') &&
				isFeatureEnabled('confluence', 'confluence-ai') && (
					<RovoHeaderImageGenerator
						generateAIHeaderImage={generateAIHeaderImage}
						prefillText={suggestedPrompt || undefined}
						onSubmit={({ selectedTheme }) => {
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'headerImageGenerationSubmitted',
									source: 'editorPageCoverPicture',
									attributes: {
										selectedTheme,
									},
								},
							}).fire();
						}}
						moreActionsButton={
							<Tooltip content={intl.formatMessage(i18n.suggestPromptTooltip)} position="bottom">
								<Button
									type="button"
									appearance="subtle"
									iconBefore={<LightbulbIcon label="" />}
									isDisabled={Boolean(userOffline) || isSuggestingPrompt}
									onClick={handleSuggestImagePromptClick}
									testId="suggest-ai-image-prompt-button"
								>
									<FormattedMessage {...i18n.suggestPromptLabel} />
								</Button>
							</Tooltip>
						}
					/>
				)}
			{typeof onDelete === 'function' ? (
				<DeleteButtonWrapper>
					<Button
						appearance="subtle"
						iconBefore={<DeleteIcon label="" />}
						shouldFitContainer
						onClick={onDelete}
						isDisabled={userOffline}
						testId="remove-image-button"
					>
						<MenuOptionsWrapper>
							<FormattedMessage {...i18n.removeHeaderButton} />
						</MenuOptionsWrapper>
					</Button>
				</DeleteButtonWrapper>
			) : null}
		</Stack>
	);
};
